import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Camera, Loader2, Check, X, Sparkles, AlertCircle, Layers,
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';
import { flagFor } from '../lib/flags';
import { useI18n } from '../lib/i18n';

const MAX_DIMENSION = 1280;
const MAX_UPLOAD_BYTES = 4 * 1024 * 1024;

async function prepareForUpload(file: File): Promise<File> {
  if (!file.type.startsWith('image/') && !/\.heic$/i.test(file.name)) return file;
  let working: File = file;
  if (file.type === 'image/heic' || /\.heic$/i.test(file.name)) {
    try {
      const heic2any = (await import('heic2any')).default;
      const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      const jpegBlob = Array.isArray(blob) ? blob[0] : blob;
      working = new File(
        [jpegBlob], file.name.replace(/\.heic$/i, '.jpg'),
        { type: 'image/jpeg', lastModified: Date.now() },
      );
    } catch { return file; }
  }
  const bitmap = await createImageBitmap(working).catch(() => null);
  if (!bitmap) return working;
  const longest = Math.max(bitmap.width, bitmap.height);
  const scale = longest > MAX_DIMENSION ? MAX_DIMENSION / longest : 1;
  if (scale === 1 && working.size <= 400 * 1024) {
    bitmap.close?.(); return working;
  }
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) { bitmap.close?.(); return working; }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  const blob: Blob | null = await new Promise((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.82),
  );
  if (!blob) return working;
  return new File([blob], working.name.replace(/\.[^.]+$/, '') + '.jpg', {
    type: 'image/jpeg', lastModified: Date.now(),
  });
}

type Step = 'choose' | 'preview' | 'analyzing' | 'review' | 'done';

// Mockup visual del 4×4 de dorsos de stickers
const SAMPLE_CODES = [
  'ARG 5', 'BRA 12', 'FRA 3', 'MEX 1',
  'HAI 7', 'ESP 9', 'GER 15', 'USA 11',
  'POR 20', 'CAN 4', 'ITA 17', 'NED 8',
  'JPN 13', 'KOR 2', 'BEL 6', 'URU 10',
];

function ExampleLooseGrid() {
  return (
    <div className="grid grid-cols-4 gap-1.5 p-2 rounded-xl bg-foam/90">
      {SAMPLE_CODES.map((code) => (
        <div
          key={code}
          className="aspect-[3/4] rounded-md bg-gradient-to-b from-stone-100 to-stone-200 border border-stone-300/80 shadow-sm relative grid place-items-center"
        >
          <span className="absolute top-0.5 right-1 font-mono text-[7px] font-extrabold text-stone-700">
            {code}
          </span>
          <div className="text-[6px] tracking-widest text-stone-400 font-bold">
            FIFA
          </div>
          <span className="absolute bottom-0.5 left-1/2 -translate-x-1/2 text-[5px] text-stone-500 font-bold tracking-widest">
            PANINI
          </span>
        </div>
      ))}
    </div>
  );
}

export function UploadLoose() {
  const { token } = useAuth();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { t } = useI18n();
  const inputRef = useRef<HTMLInputElement>(null);

  const [step, setStep] = useState<Step>('choose');
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [pickError, setPickError] = useState<string | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [result, setResult] = useState<{
    uploadId: string;
    matches: { id: string; code: string; number: number; player_name: string | null; team: string | null }[];
    confidence: number;
  } | null>(null);
  // Excluded indexa por POSICIÓN en matches (no por sticker_id), para soportar
  // múltiples copias del mismo sticker — ej. 3 cards con HAI-7 → cada una se
  // excluye independientemente.
  const [excluded, setExcluded] = useState<Set<number>>(new Set());

  const uploadMut = useMutation({
    mutationFn: async (f: File) => {
      if (!token) throw new Error('no_session');
      const up = await api.upload(f, token);
      const an = await api.analyzeLoose(up.uploadId, token);
      return an;
    },
    onSuccess: (r) => {
      setResult(r);
      setExcluded(new Set());
      setStep('review');
    },
    onError: () => setStep('preview'),
  });

  const confirmMut = useMutation({
    mutationFn: async () => {
      if (!result) return;
      // Map por index — incluye duplicados (si la misma id aparece 3 veces, se envía 3 veces)
      const ids = result.matches
        .map((m, i) => ({ id: m.id, i }))
        .filter((x) => !excluded.has(x.i))
        .map((x) => x.id);
      return api.confirmLoose(result.uploadId, ids, token!);
    },
    onSuccess: () => {
      confetti({
        particleCount: 140, spread: 75, origin: { y: 0.4 },
        colors: ['#5ee0ff', '#c6ff5e', '#ffd166'],
      });
      qc.invalidateQueries({ queryKey: ['collection'] });
      qc.invalidateQueries({ queryKey: ['duplicates'] });
      setStep('done');
      setTimeout(() => nav('/app/duplicates'), 1500);
    },
  });

  function chooseFile() { setPickError(null); inputRef.current?.click(); }
  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const raw = e.target.files?.[0];
    e.target.value = '';
    if (!raw) return;
    setPickError(null);
    setOptimizing(true);
    try {
      const optimized = await prepareForUpload(raw);
      if (optimized.size > MAX_UPLOAD_BYTES) {
        setPickError(`La foto pesa ${(optimized.size / 1024 / 1024).toFixed(1)}MB.`);
        return;
      }
      setFile(optimized);
      setPreviewUrl(URL.createObjectURL(optimized));
      setStep('preview');
    } catch {
      setPickError('No se pudo leer la foto.');
    } finally {
      setOptimizing(false);
    }
  }
  function startAnalyze() {
    if (!file) return;
    setStep('analyzing');
    uploadMut.mutate(file);
  }
  function toggleStickerAt(idx: number) {
    setExcluded((s) => {
      const next = new Set(s);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }

  const keepCount = result ? result.matches.length - excluded.size : 0;

  // Scroll al top en cada cambio de paso (evita que quede a la mitad y parezca colgada)
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step]);

  return (
    <div className="space-y-5">
      <header>
        <p className="text-2xs uppercase tracking-widest text-haze">{t('loose.eyebrow')}</p>
        <h1 className="font-display font-extrabold text-3xl flex items-center gap-2">
          {t('loose.title1')} <span className="text-gradient">{t('loose.titleAccent')}</span> {t('loose.title2')}
        </h1>
      </header>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={onFile}
      />

      <AnimatePresence mode="wait">
        {step === 'choose' && (
          <motion.div
            key="choose"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="space-y-3"
          >
            {/* Mockup visual del 4×4 ARRIBA — para que se vea primero */}
            <div className="glass-strong rounded-2xl p-4 space-y-3 border border-cyan/30 shadow-[0_0_24px_-12px_rgba(94,224,255,0.5)]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-cyan font-semibold text-sm">
                  <Sparkles size={14} /> {t('loose.tips.exampleLabel')}
                </div>
                <span className="text-2xs text-haze">{t('loose.tips.t2')}</span>
              </div>
              <ExampleLooseGrid />
              <ul className="text-2xs text-haze space-y-1 ml-1 pt-2 border-t border-line/30">
                <li>• {t('loose.tips.t1')}</li>
                <li>• {t('loose.tips.t3')}</li>
                <li>• {t('loose.tips.t4')}</li>
              </ul>
            </div>

            <button
              onClick={chooseFile}
              disabled={optimizing}
              className="relative overflow-hidden w-full rounded-3xl glass-strong p-6 flex flex-col items-start gap-3 hover:bg-graphite/40 transition group disabled:opacity-50 text-left"
            >
              <div className="grid h-14 w-14 place-items-center rounded-2xl bg-cyan/15 text-cyan border border-cyan/40 group-hover:bg-cyan/25 transition">
                <Layers size={26} />
              </div>
              <div>
                <div className="font-display font-extrabold text-xl">
                  {optimizing ? t('upload.optimizing') : t('loose.choose')}
                </div>
                <div className="text-sm text-haze mt-1">
                  {t('loose.chooseDesc')}
                </div>
              </div>
              <span className="btn-royal w-full mt-2 text-sm justify-center">
                <Camera size={14} /> {optimizing ? '…' : t('loose.choosePhoto')}
              </span>
              <div className="pointer-events-none absolute -top-10 -right-10 h-32 w-32 rounded-full bg-cyan/20 blur-2xl group-hover:bg-cyan/35 transition" />
            </button>

            {pickError && (
              <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame">{pickError}</div>
              </div>
            )}
          </motion.div>
        )}

        {step === 'preview' && previewUrl && (
          <motion.div key="prev" initial={{opacity:0}} animate={{opacity:1}}
            className="glass-strong rounded-3xl p-4">
            <img src={previewUrl} className="rounded-2xl w-full" alt="preview" />
            {uploadMut.isError && (
              <div className="mt-3 flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame break-words">
                  {(uploadMut.error as Error)?.message ?? 'Error analizando'}
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <button className="btn-ghost flex-1" onClick={chooseFile}>{t('common.cancel')}</button>
              <button className="btn-primary flex-1" onClick={startAnalyze}>
                <Sparkles size={16} /> {uploadMut.isError ? t('common.retry') : t('upload.gallery.analyze')}
              </button>
            </div>
          </motion.div>
        )}

        {step === 'analyzing' && (
          <motion.div key="ana" initial={{opacity:0}} animate={{opacity:1}}
            className="glass-strong rounded-3xl p-10 text-center">
            <Loader2 size={36} className="mx-auto animate-spin text-cyan" />
            <div className="mt-4 font-display font-bold text-xl">{t('loose.analyzing.title')}</div>
            <div className="mt-1 text-sm text-haze">{t('loose.analyzing.subtitle')}</div>
            <div className="mt-4 progress-bar"><div className="progress-fill" style={{ width: '60%' }} /></div>
          </motion.div>
        )}

        {step === 'review' && result && (
          <motion.div key="rev" initial={{opacity:0,y:8}} animate={{opacity:1,y:0}}
            className="space-y-4"
          >
            <div className="glass-strong rounded-3xl p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-2xs uppercase tracking-widest text-haze">{t('upload.review.eyebrow')}</p>
                  <h2 className="font-display font-extrabold text-2xl mt-0.5">
                    {result.matches.length} {result.matches.length === 1 ? t('loose.review.dupeDetected') : t('loose.review.dupesDetected')}
                  </h2>
                  <p className="mt-1 text-sm text-haze">
                    {t('loose.review.subtitle')} <span className="text-cyan font-semibold">{t('loose.review.plusOne')}</span>.
                  </p>
                </div>
                <span className="chip-cyan shrink-0">
                  {Math.round(result.confidence * 100)}%
                </span>
              </div>
            </div>

            {result.matches.length === 0 ? (
              <div className="glass rounded-2xl p-6 text-center text-haze text-sm">
                {t('loose.review.noStickers')}
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {result.matches.map((m, idx) => {
                  const isOut = excluded.has(idx);
                  const code = m.code?.replace(/-/g, ' ') ?? `#${m.number}`;
                  const flag = flagFor(m.team);
                  // Indicador de "copia N" si el mismo code aparece varias veces
                  const sameCodeOccurrences = result.matches
                    .map((x, i) => ({ x, i }))
                    .filter((y) => y.x.id === m.id);
                  const copyIndex = sameCodeOccurrences.findIndex((y) => y.i === idx) + 1;
                  const totalCopies = sameCodeOccurrences.length;
                  return (
                    <button
                      key={`${m.id}-${idx}`}
                      onClick={() => toggleStickerAt(idx)}
                      className={`relative rounded-xl p-3 text-left transition ${
                        isOut
                          ? 'bg-void/40 border border-line text-haze'
                          : 'bg-cyan/10 border border-cyan/45 text-foam shadow-[0_0_18px_-6px_rgba(94,224,255,0.5)]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`font-mono font-extrabold text-sm tabular-nums ${isOut ? 'line-through' : 'text-cyan'}`}>
                          {code}
                        </span>
                        {!isOut ? (
                          <Check size={14} className="text-cyan shrink-0" />
                        ) : (
                          <X size={14} className="text-haze shrink-0" />
                        )}
                      </div>
                      {(m.team || m.player_name) && (
                        <div className={`mt-1 text-2xs line-clamp-1 ${isOut ? 'text-haze/60' : 'text-haze'} flex items-center gap-1`}>
                          {flag && <span>{flag}</span>}
                          {m.player_name ?? m.team}
                        </div>
                      )}
                      {totalCopies > 1 && (
                        <div className={`mt-1 text-[9px] font-bold uppercase tracking-widest ${isOut ? 'text-haze/50' : 'text-cyan/80'}`}>
                          copia {copyIndex}/{totalCopies}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {confirmMut.isError && (
              <div className="flex items-start gap-2 rounded-xl border border-flame/40 bg-flame/10 p-3 text-sm">
                <AlertCircle size={18} className="mt-0.5 shrink-0 text-flame" />
                <div className="text-flame">{(confirmMut.error as Error)?.message}</div>
              </div>
            )}

            <div className="flex gap-2 sticky bottom-24">
              <button className="btn-ghost flex-1" onClick={() => setStep('choose')}>{t('common.cancel')}</button>
              <button
                className="btn-primary flex-1"
                onClick={() => confirmMut.mutate()}
                disabled={keepCount === 0 || confirmMut.isPending}
              >
                {confirmMut.isPending ? t('common.saving') : `${t('loose.review.sumCta')} ${keepCount} ${keepCount === 1 ? t('loose.review.dupe') : t('loose.review.dupes')}`}
              </button>
            </div>
          </motion.div>
        )}

        {step === 'done' && (
          <motion.div key="done" initial={{scale:0.9,opacity:0}} animate={{scale:1,opacity:1}}
            className="glass-strong rounded-3xl p-10 text-center">
            <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-cyan/20 text-cyan animate-pulseglow">
              <Check size={32} />
            </div>
            <div className="mt-4 font-display font-extrabold text-2xl text-gradient">{t('loose.done.title')}</div>
            <div className="mt-1 text-sm text-haze">{t('loose.done.subtitle')}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
